import { iframeDataBridge } from '@/iframe/iframeDataBridge';
import { pinia } from '@/stores';
import { useAppointmentStore } from '@/stores/AppointmentStore';
import { toDayKey } from '@/utils/legacy/agendaRouteSource';

// Writes from the route optimization feature (#23500) into the legacy agenda.

const legacySales = () => iframeDataBridge.getIframeWindow()?.externis?.sales;

/**
 * Lets the legacy agenda eventRender read the pins kept in AppointmentStore (RG-23). Re-run on every
 * iframe load / planning view: externis.sales is rebuilt with the legacy document.
 * The agenda passes the visit start, so a pinned visit moved to another day keeps its pin until that day.
 */
export function exposePinsToLegacy() {
	const sales = legacySales();
	if (!sales) return;
	const store = useAppointmentStore(pinia);
	sales.pinnedAppointments = {
		isPinned: (uid, start) => {
			if (!store.isPinned(uid)) return false;
			if (start) store.followPin(uid, toDayKey(start));
			return true;
		},
	};
}

/** Re-renders the legacy FullCalendar events so the pin icon follows a pin change. */
export function rerenderLegacyAgenda() {
	legacySales()?.vmAgenda?.calendar?.rerenderEvents?.();
}

/**
 * Applies the accepted proposal through vwSFAPlanning.applyOptimizedRoute (RG-05, RG-21): sequential
 * full-object saves of the moved visits, then removal of the unplaceable visits the user chose to delete,
 * then an agenda refresh. Never partial-silent: failures are returned to the caller.
 *
 * @param {{changes: {uid: string, start: number, end: number}[], deletions: string[]}} payload
 * @returns {Promise<{ok: number, failed: string[]}>}
 */
export async function applyOptimization({ changes = [], deletions = [] }) {
	const vm = iframeDataBridge.getIframeWindow()?.externis?.ecosVM?.currentViewModel;
	if (typeof vm?.applyOptimizedRoute !== 'function') throw new Error('Planning view not available');
	const results = await vm.applyOptimizedRoute(
		changes.map((c) => ({ uid: c.uid, start: c.start, end: c.end })),
		deletions.slice()
	);
	const list = Array.from(results || []);
	return { ok: list.filter((r) => r.status === 'fulfilled').length, failed: list.filter((r) => r.status !== 'fulfilled').map((r) => r.reason || 'error') };
}
